
import React from "react";
import { Pagination as UIPagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
}: PaginationProps) => {
  // If no pages or only one page, don't show pagination
  if (totalPages <= 1) return null;

  // For simple pagination with 7 or fewer pages
  if (totalPages <= 7) {
    return (
      <UIPagination className={className}>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => onPageChange(currentPage - 1)}
              className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
            />
          </PaginationItem>

          {Array.from({ length: totalPages }).map((_, i) => (
            <PaginationItem key={i + 1}>
              <PaginationLink
                isActive={currentPage === i + 1}
                onClick={() => onPageChange(i + 1)}
              >
                {i + 1}
              </PaginationLink>
            </PaginationItem>
          ))}

          <PaginationItem>
            <PaginationNext
              onClick={() => onPageChange(currentPage + 1)}
              className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
            />
          </PaginationItem>
        </PaginationContent>
      </UIPagination>
    );
  }

  // For complex pagination with more than 7 pages
  let pageItems = [];

  // Always show first page
  pageItems.push(
    <PaginationItem key="first">
      <PaginationLink
        isActive={currentPage === 1}
        onClick={() => onPageChange(1)}
      >
        1
      </PaginationLink>
    </PaginationItem>
  );

  // Add ellipsis if not starting from page 2
  if (currentPage > 3) {
    pageItems.push(
      <PaginationItem key="ellipsis-start">
        <PaginationEllipsis />
      </PaginationItem>
    );
  }

  // Calculate range of pages to show around current page
  let startPage = Math.max(2, currentPage - 1);
  let endPage = Math.min(totalPages - 1, currentPage + 1);

  // Adjust to show at least 3 pages if possible
  if (endPage - startPage < 2) {
    if (startPage === 2) {
      endPage = Math.min(totalPages - 1, startPage + 2);
    } else if (endPage === totalPages - 1) {
      startPage = Math.max(2, endPage - 2);
    }
  }

  // Add middle pages
  for (let i = startPage; i <= endPage; i++) {
    pageItems.push(
      <PaginationItem key={i}>
        <PaginationLink
          isActive={currentPage === i}
          onClick={() => onPageChange(i)}
        >
          {i}
        </PaginationLink>
      </PaginationItem>
    );
  }

  // Add ellipsis if not ending at the last page - 1
  if (currentPage < totalPages - 2) {
    pageItems.push(
      <PaginationItem key="ellipsis-end">
        <PaginationEllipsis />
      </PaginationItem>
    );
  }

  // Always show last page
  if (totalPages > 1) {
    pageItems.push(
      <PaginationItem key="last">
        <PaginationLink
          isActive={currentPage === totalPages}
          onClick={() => onPageChange(totalPages)}
        >
          {totalPages}
        </PaginationLink>
      </PaginationItem>
    );
  }

  return (
    <UIPagination className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => onPageChange(currentPage - 1)}
            className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
          />
        </PaginationItem>

        {pageItems}

        <PaginationItem>
          <PaginationNext
            onClick={() => onPageChange(currentPage + 1)}
            className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
          />
        </PaginationItem>
      </PaginationContent>
    </UIPagination>
  );
};

export default Pagination;
